import { useEffect, useRef, useState } from 'preact/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation } from 'wouter-preact'
import {
  emailTranscript,
  getConversation,
  getConversationQuality,
  listSites,
  sendReply,
  sendTypingPing,
} from '../api/endpoints'
import { Badge, Banner, Button, SiteBadge, StatusPill } from '../components/ui'
import { formatTimestamp, timeAgo } from '../utils/time'
import type { ConversationDetail, ConversationScore, Message } from '../types'

export function ConversationDetailPage({ id }: { id: string }) {
  const [, setLocation] = useLocation()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const threadEndRef = useRef<HTMLDivElement>(null)

  // "Send by email" toolbar state — collapsed to a single button by
  // default; expands to an editable recipient + Send/Cancel when opened.
  const [emailOpen, setEmailOpen] = useState(false)
  const [emailTo, setEmailTo] = useState('')
  const [emailPrefilled, setEmailPrefilled] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  // Retain the last-sent recipient so the success banner can name it —
  // otherwise a follow-up send would just show "sent" without context.
  const [emailLastSentTo, setEmailLastSentTo] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['conversation', id],
    queryFn: ({ signal }) => getConversation(id, signal),
    refetchInterval: (q) =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden'
        ? false
        : 6_000,
  })

  const reply = useMutation({
    mutationFn: (message: string) => sendReply(id, message),
    onSuccess: (res) => {
      setDraft('')
      setError(null)
      if (res.delivery === 'late_email') {
        // Tell user but don't append anything; session is timed_out
        setError(
          'Session had timed out — your reply was sent to the visitor as an email instead.'
        )
      }
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Reply failed')
    },
  })

  // Sites cache — used to prefill the transcript recipient with the current
  // conversation's site.email. Same query key any other page uses so react-
  // query dedupes; no extra network hit if this data is already loaded.
  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: listSites })

  // This conversation's reflection score + proposals (C1/C0). Fetched once —
  // the score doesn't change, so it stays out of the 6s conversation poll.
  const qualityQ = useQuery({
    queryKey: ['conversation-quality', id],
    queryFn: () => getConversationQuality(id),
    staleTime: 60_000,
  })

  const emailMut = useMutation({
    mutationFn: (to: string) => emailTranscript(id, to),
    onSuccess: (_res, to) => {
      setEmailError(null)
      setEmailLastSentTo(to)
      // Collapse the form back to the button so the success banner is the
      // dominant thing on screen — operators told us the inline confirmation
      // next to the button was easy to miss.
      setEmailOpen(false)
    },
    onError: (err: unknown) => {
      // The worker returns `{ error, invalid?: string[], max?, given? }` for
      // recipient validation failures. Surface the offending address(es) so
      // the operator can find the typo without re-reading their input.
      const data = (err as { data?: unknown }).data as
        | { error?: string; invalid?: unknown; max?: number; given?: number }
        | undefined
      if (data?.error === 'invalid_recipient' && Array.isArray(data.invalid) && data.invalid.length) {
        setEmailError(`Invalid address: ${data.invalid.join(', ')}`)
        return
      }
      if (data?.error === 'too_many_recipients') {
        setEmailError(`Too many recipients (${data.given} given, max ${data.max}).`)
        return
      }
      setEmailError(err instanceof Error ? err.message : 'Send failed')
    },
  })

  // Autoscroll to bottom when messages arrive
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [data?.messages.length])

  // Prefill the transcript-email recipient from the site's configured
  // default the first time we know both the conversation's site and the
  // sites list. Only prefills once — subsequent edits to the field are
  // preserved (operator may swap in a colleague's address).
  useEffect(() => {
    if (emailPrefilled) return
    if (!data?.site || !sitesQ.data?.sites) return
    const siteRow = sitesQ.data.sites.find((s) => s.id === data.site)
    if (siteRow?.email) setEmailTo(siteRow.email)
    setEmailPrefilled(true)
  }, [emailPrefilled, data?.site, sitesQ.data?.sites])

  // Typing-indicator emitter — debounced. While the operator is actively
  // typing (input has content + recent keystrokes), we POST typing:true
  // every ~3s so the visitor's widget shows "team is typing". On pause
  // (no keystroke for 4s) or empty input or unmount, send typing:false
  // so the indicator clears immediately.
  const typingThrottleRef = useRef<number | null>(null)
  const typingClearRef = useRef<number | null>(null)
  const typingActiveRef = useRef<boolean>(false)
  const canEmitTyping =
    data?.status === 'escalated' || data?.status === 'human'
  useEffect(() => {
    if (!canEmitTyping) return
    const trimmed = draft.trim()

    if (trimmed.length === 0) {
      // Empty draft → clear immediately if previously active
      if (typingActiveRef.current) {
        typingActiveRef.current = false
        sendTypingPing(id, false).catch(() => {})
      }
      if (typingThrottleRef.current !== null) {
        clearTimeout(typingThrottleRef.current)
        typingThrottleRef.current = null
      }
      if (typingClearRef.current !== null) {
        clearTimeout(typingClearRef.current)
        typingClearRef.current = null
      }
      return
    }

    // First keystroke (or post-pause restart) — fire immediately
    if (!typingActiveRef.current) {
      typingActiveRef.current = true
      sendTypingPing(id, true).catch(() => {})
    } else if (typingThrottleRef.current === null) {
      // Already typing — schedule the next refresh ~3s out (KV TTL is 8s,
      // so a missed ping doesn't immediately drop the indicator).
      typingThrottleRef.current = window.setTimeout(() => {
        typingThrottleRef.current = null
        sendTypingPing(id, true).catch(() => {})
      }, 3_000)
    }

    // 4s after the last keystroke with no new ones, clear typing.
    if (typingClearRef.current !== null) clearTimeout(typingClearRef.current)
    typingClearRef.current = window.setTimeout(() => {
      typingClearRef.current = null
      typingActiveRef.current = false
      sendTypingPing(id, false).catch(() => {})
    }, 4_000)
  }, [draft, canEmitTyping, id])

  // On unmount, leave a clean state.
  useEffect(
    () => () => {
      if (typingActiveRef.current) {
        sendTypingPing(id, false).catch(() => {})
      }
      if (typingThrottleRef.current !== null) clearTimeout(typingThrottleRef.current)
      if (typingClearRef.current !== null) clearTimeout(typingClearRef.current)
    },
    [id]
  )

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <span class="spinner spinner-dark" />
      </div>
    )
  }
  if (isError || !data) {
    return (
      <Banner variant="error">
        Couldn’t load this conversation.{' '}
        <Link href="/conversations">Back to inbox</Link>
      </Banner>
    )
  }

  const visitor = data.visitorName || data.visitorEmail || 'Anonymous visitor'
  const canReply = data.status === 'escalated' || data.status === 'human'
  const timedOut = data.status === 'timed_out'

  function handleSend(e: Event) {
    e.preventDefault()
    const msg = draft.trim()
    if (!msg) return
    reply.mutate(msg)
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSend(e)
    }
  }

  function handleEmailSend(e: Event) {
    e.preventDefault()
    const to = emailTo.trim()
    if (!to) {
      setEmailError('Enter a recipient email.')
      return
    }
    setEmailError(null)
    emailMut.mutate(to)
  }

  return (
    <>
      <div style={{ marginBottom: 16 }} class="detail-back">
        <Link href="/conversations" class="nav-link" style={{ padding: 0, color: 'var(--m-action)' }}>
          ← Inbox
        </Link>
      </div>

      <div class="thread-header">
        <h2>
          <span>{visitor}</span>
          <SiteBadge site={data.site} />
          <StatusPill status={data.status} />
        </h2>
        <div class="contact-row">
          <PresenceIndicator isOnline={data.isOnline} lastSeenAt={data.lastSeenAt} />
          {data.visitorEmail && <span>✉ {data.visitorEmail}</span>}
          {data.visitorPhone && <span>☎ {data.visitorPhone}</span>}
          {data.escalatedAt && (
            <span>Escalated {timeAgo(data.escalatedAt)}</span>
          )}
          {data.handledBy && (data.status === 'human' || data.status === 'escalated') && (
            <span style={{ color: 'var(--m-success, #15803d)', fontWeight: 600 }}>
              ● {data.handledBy} is handling this
            </span>
          )}
        </div>
      </div>

      {qualityQ.data?.score && (
        <QualityStrip
          score={qualityQ.data.score}
          proposalCount={qualityQ.data.proposals.length}
        />
      )}

      <div style={{ margin: '8px 0 12px', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {!emailOpen && (
          <Button
            variant="outline"
            onClick={() => setLocation(`/tune?site=${encodeURIComponent(data.site)}&conversation=${encodeURIComponent(id)}`)}
          >
            ⚙ Tune this conversation
          </Button>
        )}
        {!emailOpen ? (
          <Button variant="outline" onClick={() => setEmailOpen(true)}>
            ✉ Send by email
          </Button>
        ) : (
          <form
            onSubmit={handleEmailSend}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', flex: 1 }}
          >
            <input
              type="text"
              value={emailTo}
              onInput={(e) => setEmailTo((e.currentTarget as HTMLInputElement).value)}
              placeholder="recipient@example.com, another@example.com"
              required
              disabled={emailMut.isPending}
              autoComplete="email"
              style={{
                flex: 1,
                minWidth: 260,
                padding: '8px 12px',
                border: '1px solid var(--m-border, #d1d5db)',
                borderRadius: 6,
                fontSize: 14,
                background: 'var(--m-bg, #fff)',
                color: 'var(--m-text, #111827)',
              }}
            />
            <Button type="submit" variant="accent" loading={emailMut.isPending}>
              Send
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={emailMut.isPending}
              onClick={() => {
                setEmailOpen(false)
                setEmailError(null)
              }}
            >
              Cancel
            </Button>
          </form>
        )}
        {emailError && (
          <div style={{ flex: '1 1 100%' }}>
            <Banner variant="error">{emailError}</Banner>
          </div>
        )}
        {emailMut.isSuccess && !emailOpen && emailLastSentTo && (
          <div style={{ flex: '1 1 100%' }}>
            <Banner variant="success">
              ✓ Transcript sent to <strong>{emailLastSentTo}</strong>
              {emailMut.data?.emailedAt ? ` · ${timeAgo(emailMut.data.emailedAt)}` : ''}
            </Banner>
          </div>
        )}
      </div>

      {timedOut && (
        <Banner variant="warn">
          This conversation timed out. Any reply you send will be emailed to the
          visitor instead of appearing in their chat.
        </Banner>
      )}

      {data.extractedLead && (
        <ExtractedLeadPanel
          lead={data.extractedLead}
          extractedAt={data.extractedLeadAt}
        />
      )}

      <div class="thread">
        {data.messages.length === 0 ? (
          <p style={{ color: 'var(--m-text-muted)' }}>No messages yet.</p>
        ) : (
          data.messages.map((m, i) => {
            // Escalation shown as an event in the thread — the divider sits
            // where the handover actually happened.
            const prev = data.messages[i - 1]
            const showEscalation =
              data.escalatedAt !== null &&
              m.timestamp >= data.escalatedAt &&
              (!prev || prev.timestamp < data.escalatedAt)
            return (
              <>
                {showEscalation && (
                  <div class="thread-event" key={`esc-${i}`}>
                    <s />
                    <span>Escalated to the team</span>
                    <s />
                  </div>
                )}
                <MessageBubble key={i} m={m} />
              </>
            )
          })
        )}
        <div ref={threadEndRef} />
      </div>

      <div class="composer safe-bottom" aria-hidden={!canReply && !timedOut}>
        <form class="composer-inner" onSubmit={handleSend}>
          <textarea
            class="textarea"
            placeholder={
              timedOut
                ? 'Send a reply as email…'
                : 'Write a reply…  (⌘/Ctrl + Enter to send)'
            }
            value={draft}
            onInput={(e) =>
              setDraft((e.currentTarget as HTMLTextAreaElement).value)
            }
            onKeyDown={handleKeyDown}
            rows={2}
            disabled={reply.isPending}
          />
          <Button
            type="submit"
            variant={timedOut ? 'outline' : 'accent'}
            size="lg"
            loading={reply.isPending}
            disabled={!draft.trim()}
          >
            Send
          </Button>
        </form>
        {error && (
          <div class="composer-status">
            <Banner variant="error">{error}</Banner>
          </div>
        )}
      </div>
    </>
  )
}

function MessageBubble({ m }: { m: Message }) {
  const isUser = m.role === 'user'
  const isHuman = !isUser && m.source === 'human'
  const cls = isUser ? 'msg msg-user' : isHuman ? 'msg msg-human' : 'msg msg-bot'
  const who = isUser ? 'Visitor' : isHuman ? (m.operatorName || 'Team') : 'Bot'
  // Bot messages may contain markdown links + bold (per renderBotMarkdown in
  // the widget); render them as HTML so operators can click through to the
  // products the bot recommended. Visitor + human-operator messages stay
  // plain text — no XSS surface.
  const isBot = !isUser && !isHuman
  return (
    <div class={cls}>
      {isBot
        ? <div dangerouslySetInnerHTML={{ __html: renderBotMarkdown(m.content) }} />
        : <div>{m.content}</div>}
      <div class="msg-meta">
        {who} · {formatTimestamp(m.timestamp)}
      </div>
      {isBot && m.meta?.sources && m.meta.sources.length > 0 && (
        <details class="msg-sources">
          <summary>
            🔎 {m.meta.sources.length} source{m.meta.sources.length === 1 ? '' : 's'}
          </summary>
          <ul>
            {m.meta.sources.map((s, i) => (
              <li key={i}>
                <b>
                  {s.type === 'web_search'
                    ? 'Manufacturer search'
                    : s.type === 'catalogue'
                      ? 'Catalogue lookup'
                      : 'Product retrieval'}
                </b>
                {s.query ? <> — “{s.query}”</> : null}
                <div class="msg-source-detail">{s.detail}</div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

// Mirrors widget/src/widget.ts:renderBotMarkdown so what the visitor saw
// is what the operator sees. Markdown surface is deliberately narrow:
// links + bold + line breaks. Anything else passes through as escaped text.
function renderBotMarkdown(text: string): string {
  let html = escapeHtml(text)
  html = html.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, url) => {
    if (!/^(https?:\/\/|mailto:)/i.test(url)) return label
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`
  })
  html = html.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/\n/g, '<br>')
  return html
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function ExtractedLeadPanel({
  lead,
  extractedAt,
}: {
  lead: Record<string, string | null>
  extractedAt: number | null
}) {
  // Show populated fields first, then "—" placeholders for missing ones.
  // Order is preserved from the schema (object property order).
  const entries = Object.entries(lead)
  const populated = entries.filter(([, v]) => v !== null && v !== '')
  const missing = entries.filter(([, v]) => v === null || v === '')
  if (populated.length === 0 && missing.length === 0) return null

  return (
    <div class="lead-panel">
      <div class="lead-panel-title">
        <span>What we know about this lead</span>
        {extractedAt && (
          <span class="lead-panel-when">
            extracted {timeAgo(extractedAt)}
          </span>
        )}
      </div>
      <dl class="lead-panel-fields">
        {populated.map(([key, value]) => (
          <div key={key} class="lead-panel-row">
            <dt>{labelForField(key)}</dt>
            <dd>{value}</dd>
          </div>
        ))}
        {missing.length > 0 && (
          <details class="lead-panel-missing">
            <summary>Not captured ({missing.length})</summary>
            <div class="lead-panel-missing-list">
              {missing.map(([key]) => (
                <span key={key}>{labelForField(key)}</span>
              ))}
            </div>
          </details>
        )}
      </dl>
    </div>
  )
}

function labelForField(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function PresenceIndicator({
  isOnline,
  lastSeenAt,
}: {
  isOnline: boolean
  lastSeenAt: number | null
}) {
  if (isOnline) {
    return (
      <span class="presence presence-online">
        <span class="presence-dot" /> Visitor is online
      </span>
    )
  }
  if (lastSeenAt) {
    return <span class="presence">Visitor last seen {timeAgo(lastSeenAt)}</span>
  }
  return null
}

/** Compact reflection-score strip with a link through to the Quality tab.
 *  Only rendered when the conversation has been scored (C1). */
function QualityStrip({
  score,
  proposalCount,
}: {
  score: ConversationScore
  proposalCount: number
}) {
  const color = (n: number | null) =>
    n === null
      ? 'var(--m-text-muted, #6b7280)'
      : n >= 80
        ? 'var(--m-success, #15803d)'
        : n >= 50
          ? 'var(--m-warn-ink, #b45309)'
          : 'var(--m-danger, #b91c1c)'
  const dim = (label: string, value: number | null) => (
    <span style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)' }}>
      {label}{' '}
      <span style={{ color: color(value), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value === null ? 'N/A' : value}
      </span>
    </span>
  )
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        margin: '8px 0 12px',
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid var(--m-border, #e5e7eb)',
        background: 'var(--m-bg-subtle, #f9fafb)',
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--m-text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        Quality
      </span>
      <span style={{ fontSize: 20, fontWeight: 700, color: color(score.composite), fontVariantNumeric: 'tabular-nums' }}>
        {score.composite}
      </span>
      {dim('esc', score.escalation)}
      {dim('grnd', score.grounding)}
      {dim('lead', score.leadApplicable ? score.leadCapture : null)}
      {dim('tone', score.tone)}
      {score.fabrication && <Badge variant="error">fabrication</Badge>}
      <Link
        href={`/quality?conversation=${encodeURIComponent(score.conversationId)}`}
        style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--m-action, #4f46e5)', textDecoration: 'none', whiteSpace: 'nowrap' }}
      >
        {proposalCount > 0
          ? `Review ${proposalCount} suggestion${proposalCount === 1 ? '' : 's'} →`
          : 'Open in Quality →'}
      </Link>
    </div>
  )
}
